import type { VocabularyTerm } from "../types/vocabulary.types";

// Enum form fields the canvas normalizes on fill. Maps a FORM field name to its
// CATALOG field (the engine_type select is backed by the drive_type catalog,
// matching the backend FieldOptionController alias). Others map 1:1.
export const ENUM_FORM_FIELDS = ["fuel_type", "engine_type", "condition", "trailer_axles"] as const;

const FIELD_TO_CATALOG: Record<string, string> = {
  engine_type: "drive_type",
};

function catalogField(field: string): string {
  return FIELD_TO_CATALOG[field] ?? field;
}

// Mirrors the backend Normalizer: lowercase, strip slashes, non-alphanumeric
// runs -> single space, trim. MUST mirror the backend Vocabulary\Normalizer
// exactly (lowercase -> preg_replace('/[^a-z0-9]+/', ' ') -> trim), or the
// client and server resolve tokens differently. E.g. "I/O" -> "i o" on BOTH
// sides (the slash becomes a space); it intentionally does NOT collapse to "io".
export function normalizeToken(value: string): string {
  return value.toLowerCase().trim().replace(/[^a-z0-9]+/g, " ").trim();
}

export interface VocabularyIndex {
  // catalogField -> (normalizedToken -> canonicalValue)
  resolve: Map<string, Map<string, string>>;
  // catalogField -> (canonicalValue -> label)
  labels: Map<string, Map<string, string>>;
}

export function buildVocabularyIndex(terms: VocabularyTerm[]): VocabularyIndex {
  const resolve = new Map<string, Map<string, string>>();
  const labels = new Map<string, Map<string, string>>();

  for (const term of terms) {
    if (!resolve.has(term.field)) resolve.set(term.field, new Map());
    if (!labels.has(term.field)) labels.set(term.field, new Map());
    const r = resolve.get(term.field)!;
    const l = labels.get(term.field)!;

    r.set(normalizeToken(term.value), term.value);
    for (const alias of term.aliases ?? []) {
      const n = normalizeToken(alias);
      if (n !== "") r.set(n, term.value);
    }
    l.set(term.value, term.label);
  }

  return { resolve, labels };
}

// raw -> canonical via normalized value/alias match; passthrough when the field
// is unmanaged/unloaded or the token has no confident match (mirrors the backend
// resolver's Match-or-passthrough used by the merge hook).
export function resolveToCanonical(index: VocabularyIndex, field: string, raw: string): string {
  const map = index.resolve.get(catalogField(field));
  if (!map) return raw;
  return map.get(normalizeToken(raw)) ?? raw;
}

export function getEnumLabel(index: VocabularyIndex, field: string, value: string): string {
  return index.labels.get(catalogField(field))?.get(value) ?? value;
}

// ─── E5c — evolving wizard fields ────────────────────────────────────────────
// Fields that accept user-added values (mirrors the backend VocabularyDefinition
// allow_user_values=true set, intersected with the canvas form fields).
// `condition` is in ENUM_FORM_FIELDS but LOCKED — excluded here.
export const EVOLVING_FORM_FIELDS = ["fuel_type", "engine_type", "trailer_axles"] as const;

// True when `value` is NOT a known canonical for the field's catalog. False for
// known values, empty strings, unmanaged fields, or an unloaded catalog (so the
// UI never false-flags during load).
// NOTE: `value` must be a canonical token (post-resolveToCanonical), not a raw
// alias — the labels map holds only canonical values. Callers (toFillValue,
// collectNewVocabularyTerms) pass canonical/slug values.
export function isNewCandidate(index: VocabularyIndex, field: string, value: string): boolean {
  if (!value) return false;
  const labels = index.labels.get(catalogField(field));
  if (!labels) return false;
  return !labels.has(value);
}

// Canonical-style token for a brand-new value: lowercase, spaces→underscores.
// "Hydrogen" -> "hydrogen", "Pod Drive" -> "pod_drive".
export function slugifyNewValue(raw: string): string {
  return normalizeToken(raw).replace(/ /g, "_");
}

// Human label from a token: "pod_drive" -> "Pod Drive".
export function humanizeToken(token: string): string {
  return token
    .replace(/_/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

// The value the canvas writes into a form field on fill. Known/alias → canonical;
// a genuinely-new EVOLVING value → a clean slug (so the stored token follows the
// catalog convention); everything else → passthrough (mirrors the old toCanonical).
export function toFillValue(index: VocabularyIndex, field: string, raw: unknown): unknown {
  if (typeof raw !== "string" || !(ENUM_FORM_FIELDS as readonly string[]).includes(field)) {
    return raw;
  }
  const canonical = resolveToCanonical(index, field, raw);
  if ((EVOLVING_FORM_FIELDS as readonly string[]).includes(field) && isNewCandidate(index, field, canonical)) {
    return slugifyNewValue(raw);
  }
  return canonical;
}

// ─── Display label (proper-case, catalog-authoritative) ──────────────────────
// The single helper every display surface uses to render a coded field value.
// raw → canonical (alias/casing via resolveToCanonical) → catalog label
// (authoritative proper case, e.g. "I/O - Stern Drive", "LPG"); when the
// field/value has no catalog entry, fall back to a Title-Case humanization of
// the canonical token. Empty/nullish → "" (callers keep their own "—"
// placeholder). Reuses catalogField so engine_type → drive_type.
export function getFieldLabel(index: VocabularyIndex, field: string, value: unknown): string {
  if (value == null) return "";
  const raw = String(value);
  if (raw === "") return "";
  const canonical = resolveToCanonical(index, field, raw);
  const label = index.labels.get(catalogField(field))?.get(canonical);
  return label ?? humanizeToken(canonical);
}

// Catalog-fed dropdown options for a managed field, in the catalog's term order.
// [] for unmanaged/empty/unloaded fields. Uses catalogField so engine_type →
// the drive_type catalog, matching getFieldLabel/resolveToCanonical.
export function getFieldOptions(index: VocabularyIndex, field: string): Array<{ value: string; label: string }> {
  const labels = index.labels.get(catalogField(field));
  if (!labels) return [];
  return Array.from(labels.entries()).map(([value, label]) => ({ value, label }));
}

// Gather the provisional catalog terms to create for a form's evolving fields.
// `value` is the exact token on the form (so boat column === term value); `field`
// is the CATALOG field (engine_type → drive_type) the create endpoint expects.
export function collectNewVocabularyTerms(
  index: VocabularyIndex,
  values: Record<string, unknown>,
): Array<{ field: string; value: string; label: string }> {
  const out: Array<{ field: string; value: string; label: string }> = [];
  for (const formField of EVOLVING_FORM_FIELDS) {
    const v = values[formField];
    if (typeof v !== "string" || v === "") continue;
    if (!isNewCandidate(index, formField, v)) continue;
    out.push({ field: catalogField(formField), value: v, label: humanizeToken(v) });
  }
  return out;
}
